// apps/web/src/components/BoardView.js
import React, { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  closestCorners,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SearchBar from './SearchBar';
import MediaPanel from './MediaPanel';
import { sanitizeMediaPanel } from '../utils/mediaSanitizer';

// Gradient presets (must match CardBackgroundSelector)
const GRADIENT_PRESETS = {
  'professional-grey': 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
  'ocean-blue': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'sunset': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'forest': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
};

/**
 * Calculate luminance of a color to determine if it's light or dark
 * Uses WCAG relative luminance calculation
 */
function getLuminance(color) {
  if (!color) return 0.5;

  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    // Apply gamma correction
    const [rg, gg, bg] = [r, g, b].map(c =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    );

    // Calculate relative luminance
    return 0.2126 * rg + 0.7152 * gg + 0.0722 * bg;
  }

  // For gradients, extract first color
  if (color.includes('gradient')) {
    const match = color.match(/#[0-9a-fA-F]{6}/);
    if (match) return getLuminance(match[0]);
  }

  return 0.5;
}

/**
 * Generate card background styles from background_settings
 */
function getCardBackgroundStyle(task) {
  const bgSettings = task.background_settings || {};
  const bgType = bgSettings.type || 'none';

  const styles = {};

  // Determine background
  if (bgType === 'solid' && bgSettings.solid?.color) {
    styles.backgroundColor = bgSettings.solid.color;
  } else if (bgType === 'gradient') {
    if (bgSettings.gradient?.preset) {
      styles.background = GRADIENT_PRESETS[bgSettings.gradient.preset] || '';
    } else if (bgSettings.gradient?.custom) {
      const { startColor, endColor, direction } = bgSettings.gradient.custom;
      styles.background = `linear-gradient(${direction || '135deg'}, ${startColor || '#667eea'} 0%, ${endColor || '#764ba2'} 100%)`;
    }
  } else if (bgType === 'image' && bgSettings.image?.base64) {
    styles.backgroundImage = `url(${bgSettings.image.base64})`;
    styles.backgroundSize = 'cover';
    styles.backgroundPosition = 'center';
    if (bgSettings.image.blur > 0) {
      // Apply blur via filter (will blur entire card including text - may need adjustment)
      styles.filter = `blur(${bgSettings.image.blur}px)`;
    }
  } else if (task.color) {
    // Fallback to legacy color system
    styles.backgroundColor = task.color;
  }

  // Apply text color contrast
  let textColor = 'var(--text-primary)';
  let isLight = false;

  if (bgType === 'solid' && bgSettings.solid?.color) {
    isLight = getLuminance(bgSettings.solid.color) > 0.5;
  } else if (bgType === 'gradient') {
    const gradientValue = bgSettings.gradient?.preset
      ? GRADIENT_PRESETS[bgSettings.gradient.preset]
      : `linear-gradient(${bgSettings.gradient?.custom?.direction || '135deg'}, ${bgSettings.gradient?.custom?.startColor || '#667eea'} 0%, ${bgSettings.gradient?.custom?.endColor || '#764ba2'} 100%)`;
    isLight = getLuminance(gradientValue) > 0.5;
  } else if (bgType === 'image') {
    // Images: always use light text with shadow
    textColor = '#ffffff';
    styles.textShadow = '0 1px 3px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.6)';
  } else if (task.color) {
    isLight = getLuminance(task.color) > 0.5;
  }

  if (bgType !== 'image') {
    textColor = isLight ? '#1a2332' : '#f8fafc';
  }

  styles.color = textColor;

  // Image overlay
  if (bgType === 'image' && bgSettings.image?.overlay > 0) {
    // Create overlay using box-shadow inset
    const overlayAlpha = bgSettings.image.overlay || 0.3;
    styles.boxShadow = `inset 0 0 0 1000px rgba(0, 0, 0, ${overlayAlpha})`;
  }

  return styles;
}

// We show 3 columns: todo / doing / done
const STATUSES = [
  { key: 'todo', label: 'To Do' },
  { key: 'doing', label: 'Doing' },
  { key: 'done', label: 'Done' },
];

/**
 * Normalizes task status: maps 'in_progress' to 'doing'
 * and defaults unknown or empty statuses to 'todo'.
 */
function normaliseStatus(status) {
  // Aggressively trim and lower-case the status string immediately.
  const s = (status || '').toString().trim().toLowerCase(); 
  
  if (s === 'in_progress') return 'doing'; // Handles legacy
  
  // Checks if the cleaned string matches any valid status key.
  if (STATUSES.some(st => st.key === s)) return s; 
  
  return 'todo'; // Defaults unknown status to todo
}

/** Parse markdown-ish checklist and notes (FINAL ROBUST VERSION) */
function parseChecklist(desc) {
  const d = String(desc || '');
  const lines = d.split(/\r?\n/);
  const headerIdx = lines.findIndex((l) => /^#{1,6}\s*Checklist\b/i.test(l.trim()));
  if (headerIdx === -1) {
    return { above: d.trim(), items: [], below: '' };
  }
  const before = lines.slice(0, headerIdx).join('\n').trim();
  const afterHeader = lines.slice(headerIdx + 1);

  const items = [];
  let i = 0;
  
  const CHECKLIST_ITEM_REGEX = /^\s*[-*]\s*\[( |x|X)\]\s*(.*)$/i; 

  // AGGRESSIVE LOOP: Loop until the first non-checklist line that has content.
  for (; i < afterHeader.length; i++) {
    const line = afterHeader[i];
    const trimmedLine = line.trim();
    
    const m = line.match(CHECKLIST_ITEM_REGEX);
    
    if (m) {
        // This is a checklist item: add it and continue.
        items.push({
            checked: m[1].toLowerCase() === 'x',
            text: m[2] || ''
        });
    } else if (items.length > 0 && trimmedLine) {
        // If we have items AND this line has content AND it's not an item, then we break.
        break;
    }
  }
  
  // The index 'i' is now pointing to the start of the 'below' content.
  const remainder = afterHeader.slice(i).join('\n').trim();
  return { above: before, items, below: remainder };
}

/** Compose markdown-ish description from parts */
function composeChecklist({ above, items, below }) {
  const parts = [];
  const a = (above || '').trim();
  const b = (below || '').trim();
  if (a) parts.push(a);
  
  // Always include header when any checklist logic is active
  parts.push('### Checklist');
  parts.push(''); // Ensure a blank line after the header for clean markdown list start
  
  (items || []).forEach((it) => {
    const text = it.text;
    parts.push(`- [${it.checked ? 'x' : ' '}] ${text}`);
  });
  if (b) parts.push(b);
  // Join all parts with a double newline for clean markdown section separation
  return parts.join('\n'); 
}

/** Sortable task card wrapper */
function SortableTask({ task, children, isOver, showIndicatorBelow }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1, // Fully hide original when dragging (DragOverlay shows instead)
  };

  const indicatorStyle = {
    height: 3,
    background: '#3b82f6',
    borderRadius: 2,
    margin: showIndicatorBelow ? '8px 0 0 0' : '0 0 8px 0',
    animation: 'pulse 0.5s infinite alternate'
  };

  return (
    <>
      {/* Drop indicator above */}
      {isOver && !showIndicatorBelow && (
        <div style={indicatorStyle} />
      )}

      <div ref={setNodeRef} style={style} {...attributes}>
        {/* Pass drag handle ref to children */}
        {typeof children === 'function' ? children({ dragHandleRef: setActivatorNodeRef, listeners }) : children}
      </div>

      {/* Drop indicator below */}
      {isOver && showIndicatorBelow && (
        <div style={indicatorStyle} />
      )}
    </>
  );
}

/** Droppable empty column area */
function EmptyColumnDropZone({ status, isOver }) {
  const { setNodeRef } = useDroppable({
    id: `empty-${status}`,
    data: { type: 'empty-column', status }
  });

  const style = {
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed var(--border-light)',
    borderRadius: '8px',
    margin: '8px 0',
    padding: '16px',
    backgroundColor: isOver ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
    color: 'var(--text-tertiary)',
    fontSize: '13px',
    transition: 'all 0.2s ease',
  };

  const indicatorStyle = {
    height: 3,
    background: '#3b82f6',
    borderRadius: 2,
    width: '80%',
    marginBottom: '8px',
    animation: 'pulse 0.5s infinite alternate',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {isOver && <div style={indicatorStyle} />}
      {!isOver && 'Drop tasks here'}
    </div>
  );
}

/** Sortable checklist item wrapper using @dnd-kit */
function SortableChecklistItem({ id, children, isOver, showIndicatorBelow }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const indicatorStyle = {
    height: 3,
    background: '#3b82f6',
    borderRadius: 2,
    margin: showIndicatorBelow ? '4px 0 0 0' : '0 0 4px 0',
    animation: 'pulse 0.5s infinite alternate'
  };

  return (
    <>
      {/* Drop indicator above when dragging up */}
      {isOver && !showIndicatorBelow && <div style={indicatorStyle} />}

      <li ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </li>

      {/* Drop indicator below when dragging down */}
      {isOver && showIndicatorBelow && <div style={indicatorStyle} />}
    </>
  );
}

/** Inline checklist editor for a single task card */
function ChecklistInline({ task, onPatchTask }) {
  const parsed = useMemo(() => parseChecklist(task.description), [task.description]);
  const [overId, setOverId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  // Ensure the UI renders immediately if the task description contains the Checklist header
  const hasChecklist = /#{1,6}\s*Checklist\b/i.test(task.description || '');

  // Configure drag sensors for checklist (supports both mouse and touch)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Slightly less than task cards for more responsive feel
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms hold before drag starts on touch devices
        tolerance: 5, // Allow 5px of movement during the delay
      },
    })
  );

  async function addItemFromInput(ev) {
    if (ev.key !== 'Enter') return;
    const val = ev.currentTarget.value.trim();
    if (!val) return;
    const next = {
      above: parsed.above,
      items: [...parsed.items, { text: val, checked: false }],
      below: parsed.below,
    };
    ev.currentTarget.value = '';
    await onPatchTask(task.id, { description: composeChecklist(next) });
  }

  async function toggle(idx, checked) {
    const nextItems = parsed.items.map((it, i) => (i === idx ? { ...it, checked } : it));
    const next = { above: parsed.above, items: nextItems, below: parsed.below };
    await onPatchTask(task.id, { description: composeChecklist(next) });
  }

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    setOverId(event.over?.id || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setOverId(null);
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = parseInt(active.id.split('-').pop());
    const newIndex = parseInt(over.id.split('-').pop());

    // Determine drag direction
    const draggingDown = oldIndex < newIndex;

    // Calculate final index (where item will land)
    let finalIndex = newIndex;
    if (draggingDown) {
      // When dragging down, place AFTER the over item
      finalIndex = newIndex;
    }

    // Reorder items
    const items = [...parsed.items];
    const [movedItem] = items.splice(oldIndex, 1);
    items.splice(finalIndex, 0, movedItem);

    const next = {
      above: parsed.above,
      items,
      below: parsed.below,
    };

    await onPatchTask(task.id, { description: composeChecklist(next) });
  };

  if (!hasChecklist) {
    // Nothing to show on the card
    return null;
  }

  const doneCount = parsed.items.filter((i) => i.checked).length;
  const totalCount = parsed.items.length;

  // Create IDs for sortable items
  const itemIds = parsed.items.map((_, idx) => `checklist-${task.id}-${idx}`);

  return (
    <div>
      {/* Notes above checklist */}
      {parsed.above ? (
        <div className="TaskCardDesc" style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>
          {parsed.above}
        </div>
      ) : null}

      {/* Checklist */}
      <div className="TaskMetaRow small" style={{ marginBottom: 6 }}>
        <span className="TaskMetaLabel">Checklist:</span>
        <span className="TaskMetaValue">{` ${doneCount}/${totalCount} done`}</span>
      </div>

      {parsed.items.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 8px 0' }}>
              {parsed.items.map((it, idx) => {
                const itemId = `checklist-${task.id}-${idx}`;
                const isThisOver = overId === itemId;

                // Determine if dragging down (show indicator below) or up (show above)
                let showIndicatorBelow = false;
                if (isThisOver && activeId) {
                  const activeIdx = parseInt(activeId.split('-').pop());
                  const overIdx = parseInt(overId.split('-').pop());
                  showIndicatorBelow = activeIdx < overIdx;
                }

                return (
                  <SortableChecklistItem
                    key={itemId}
                    id={itemId}
                    isOver={isThisOver}
                    showIndicatorBelow={showIndicatorBelow}
                  >
                    <div className="ChecklistItem" title="Drag to reorder">
                      <input
                        type="checkbox"
                        checked={!!it.checked}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggle(idx, e.target.checked);
                        }}
                        style={{ flexShrink: 0, cursor: 'pointer' }}
                        aria-label={`Toggle ${it.text}`}
                      />
                      <div className={`ChecklistItemText ${it.checked ? 'checked' : ''}`}>
                        {it.text}
                      </div>
                      <span className="ChecklistDragHandle">⋮⋮</span>
                    </div>
                  </SortableChecklistItem>
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
      ) : null}

      {/* Inline add field (append on Enter) */}
      <input
        className="NewTaskInput"
        placeholder="Add item here… (press Enter)"
        onKeyDown={addItemFromInput}
        title="Type and press Enter"
        aria-label="Add new checklist item"
      />

      {/* Notes below checklist */}
      {parsed.below ? (
        <div className="TaskCardDesc" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
          {parsed.below}
        </div>
      ) : null}
    </div>
  );
}

export default function BoardView({
  isAuthed,
  workspace,
  list,
  tasks,
  collaborators,
  labels,
  newTaskTitle,
  setNewTaskTitle,
  newTaskDesc,
  setNewTaskDesc,
  onCreateTask,
  onChangeStatus,
  onOpenTask,
  onPatchTask,
  onOpenTemplates,
  onReorderTask,
}) {
  // Track which drop zone is being hovered over
  const [overId, setOverId] = useState(null);
  // Track which task is being dragged
  const [activeId, setActiveId] = useState(null);
  // Track mouse Y position during drag
  const [mouseY, setMouseY] = useState(0);
  // Track which task's media panel is open
  const [openMediaPanelTask, setOpenMediaPanelTask] = useState(null);

  // Use closestCorners for better vertical list collision detection
  const customCollisionDetection = closestCorners;

  // Configure drag sensors (supports both mouse and touch)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // 3px movement before drag starts (more responsive)
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms hold before drag starts on touch devices
        tolerance: 5, // Allow 5px of movement during the delay
      },
    })
  );

  // Group tasks by status and sort by position
  const grouped = useMemo(() => {
    const buckets = { todo: [], doing: [], done: [] };
    (tasks || []).forEach((t) => {
      // Use normaliseStatus for consistent grouping
      const s = normaliseStatus(t.status);
      (buckets[s] || buckets.todo).push(t);
    });
    // Sort each bucket by position
    Object.keys(buckets).forEach((key) => {
      buckets[key].sort((a, b) => (a.position || 0) - (b.position || 0));
    });
    return buckets;
  }, [tasks]);

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);

    // Add mouse/touch move listeners to track cursor position
    const handleMouseMove = (e) => {
      setMouseY(e.clientY);
    };

    const handleTouchMove = (e) => {
      if (e.touches[0]) {
        setMouseY(e.touches[0].clientY);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove);

    // Store cleanup function
    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
    };
    // We'll clean this up in handleDragEnd
    window._dragCleanup = cleanup;
  };

  // Handle drag over
  const handleDragOver = (event) => {
    setOverId(event.over?.id || null);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setOverId(null);
    setActiveId(null);
    setMouseY(0);

    // Clean up mouse listener
    if (window._dragCleanup) {
      window._dragCleanup();
      window._dragCleanup = null;
    }

    if (!over || !onReorderTask) return;

    const activeId = active.id;
    const overId = over.id;

    // Find the task being dragged
    const activeTask = tasks.find((t) => t.id === activeId);
    if (!activeTask) return;

    // Check if dropping on empty column
    if (overId.startsWith('empty-')) {
      const newStatus = overId.replace('empty-', '');
      const destTasks = grouped[newStatus] || [];

      // Add to end of empty column
      const newPosition = destTasks.length > 0
        ? (destTasks[destTasks.length - 1].position || 0) + 1000
        : 1000;

      onReorderTask(activeId, newPosition, newStatus);
      return;
    }

    // Dropping on another task
    const overTask = tasks.find((t) => t.id === overId);
    if (!overTask) return;

    // If same task, no-op
    if (activeId === overId) return;

    const newStatus = overTask.status;
    const destTasks = grouped[newStatus] || [];

    // Remove active task from destination list for calculation
    const destTasksWithoutActive = destTasks.filter((t) => t.id !== activeId);
    const overIndexInFiltered = destTasksWithoutActive.findIndex((t) => t.id === overId);

    // Determine if dragging down (show indicator below) or up (show indicator above)
    const activeIndex = destTasks.findIndex((t) => t.id === activeId);
    const overIndex = destTasks.findIndex((t) => t.id === overId);

    let draggingDown = false;

    // For same-column: dragging down if activeIndex < overIndex
    if (activeIndex !== -1) {
      draggingDown = activeIndex < overIndex;
    } else {
      // Cross-column: check mouse position relative to the over item
      if (mouseY > 0) {
        const cardElement = document.querySelector(`[data-task-id="${overId}"]`);
        if (cardElement) {
          const rect = cardElement.getBoundingClientRect();
          const cardMiddle = rect.top + rect.height / 2;

          // Place after if mouse was in bottom half
          draggingDown = mouseY > cardMiddle;
        }
      }
    }

    let newPosition;

    if (destTasksWithoutActive.length === 0) {
      newPosition = 1000;
    } else if (draggingDown) {
      // Place AFTER the over task
      if (overIndexInFiltered === destTasksWithoutActive.length - 1) {
        const lastTask = destTasksWithoutActive[overIndexInFiltered];
        newPosition = (lastTask.position || 0) + 1000;
      } else {
        const taskBefore = destTasksWithoutActive[overIndexInFiltered];
        const taskAfter = destTasksWithoutActive[overIndexInFiltered + 1];
        newPosition = ((taskBefore.position || 0) + (taskAfter.position || 0)) / 2;
      }
    } else {
      // Place BEFORE the over task
      if (overIndexInFiltered === 0) {
        const firstTask = destTasksWithoutActive[0];
        newPosition = (firstTask.position || 1000) - 500;
      } else {
        const taskBefore = destTasksWithoutActive[overIndexInFiltered - 1];
        const taskAfter = destTasksWithoutActive[overIndexInFiltered];
        newPosition = ((taskBefore.position || 0) + (taskAfter.position || 0)) / 2;
      }
    }

    onReorderTask(activeId, newPosition, newStatus);
  };

  return (
    <main className="BoardView" role="main" aria-label="Task Board">
      {!isAuthed ? (
        <div className="BoardPlaceholder">
          <h2>Not logged in</h2>
          <p>Use "Test Login (Sentia)" in the top bar.</p>
        </div>
      ) : !workspace ? (
        <div className="BoardPlaceholder">
          <h2>No workspace selected</h2>
          <p>Pick or create a workspace in the left sidebar.</p>
        </div>
      ) : !list ? (
        <div className="BoardPlaceholder">
          <h2>No list / board selected</h2>
          <p>Pick or create a list in the left sidebar.</p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="BoardHeader">
            <div>
              <div className="BoardTitle">
                {list.title || list.name || list.list_name || 'Untitled List'}
              </div>
              <div className="BoardSubTitle">
                In workspace: {workspace.name || workspace.workspace_name || 'Untitled Workspace'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="Btn" onClick={onOpenTemplates} title="Add Template" aria-label="Add template to list">
                + Add Template
              </button>
            </div>
          </div>

          {/* Search Bar */}
          {workspace && (
            <div style={{ padding: '0 20px', marginTop: 20 }}>
              <SearchBar
                workspaceId={workspace.id}
                labels={labels || []}
                collaborators={collaborators || []}
                onTaskSelect={(task) => onOpenTask(task)}
              />
            </div>
          )}

          {/* New task form */}
          <div className="NewTaskCard" role="form" aria-label="Create new task">
            <div className="NewTaskHeading">Add a task</div>
            <input
              className="NewTaskInput"
              placeholder="Task title"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onCreateTask();
                }
              }}
              aria-label="Task title"
            />
            <textarea
              className="NewTaskTextarea"
              placeholder="Optional description"
              value={newTaskDesc}
              onChange={(e) => setNewTaskDesc(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  onCreateTask();
                }
              }}
              aria-label="Task description"
            />
            <button className="Btn primary fullwidth" onClick={onCreateTask} aria-label="Create new task">
              + Create Task
            </button>
          </div>

          {/* Columns with drag-and-drop */}
          <DndContext 
            sensors={sensors} 
            collisionDetection={customCollisionDetection} 
            onDragStart={handleDragStart} 
            onDragOver={handleDragOver} 
            onDragEnd={handleDragEnd}
          >
            {/* FIX: Wrap the two adjacent elements in a fragment to resolve the error */}
            <>
              <div className="BoardColumns">
                {STATUSES.map((col) => {
                  const columnTasks = grouped[col.key] || [];
                  const taskIds = columnTasks.map((t) => t.id);

                  return (
                    <div key={col.key} className="BoardColumn" role="region" aria-label={`${col.label} tasks`}>
                      <div className="BoardColumnHeader">{col.label}</div>

                      {columnTasks.length === 0 ? (
                        // Empty column - show drop zone
                        <EmptyColumnDropZone
                          status={col.key}
                          isOver={overId === `empty-${col.key}`}
                        />
                      ) : (
                        // Column with cards
                        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
                          {columnTasks.map((task, taskIndex) => {
                            // Determine if we're hovering over this task
                            const isThisOver = overId === task.id;

                            // Determine if indicator should be below (dragging down)
                            let showIndicatorBelow = false;
                            if (isThisOver && activeId && mouseY > 0) {
                              const activeIndex = columnTasks.findIndex((t) => t.id === activeId);
                              const overIndex = taskIndex;

                              if (activeIndex === -1) {
                                // Cross-column drag - use mouse position relative to card
                                // Get the card element to calculate its position
                                const cardElement = document.querySelector(`[data-task-id="${task.id}"]`);
                                if (cardElement) {
                                  const rect = cardElement.getBoundingClientRect();
                                  const cardMiddle = rect.top + rect.height / 2;

                                  // Show indicator below if mouse is in bottom half of card
                                  showIndicatorBelow = mouseY > cardMiddle;
                                }
                              } else {
                                // Same column: show indicator below if dragging down
                                showIndicatorBelow = activeIndex < overIndex;
                              }
                            }

                            return (
                              <SortableTask
                                key={task.id}
                                id={task.id}
                                task={task}
                                isOver={isThisOver}
                                showIndicatorBelow={showIndicatorBelow}
                              >
                                {({ dragHandleRef, listeners }) => (
                                  <div
                                    ref={dragHandleRef}
                                    {...listeners}
                                    className="TaskCard"
                                    data-task-id={task.id}
                                    onClick={() => onOpenTask(task)}
                                    role="article"
                                    aria-label={`Task: ${task.title || 'Untitled task'}`}
                                    style={{
                                      cursor: 'grab',
                                      ...getCardBackgroundStyle(task)
                                    }}
                                  >
                                    {/* Task header with title and icons */}
                                    <div className="TaskCardHeader">
                                      {/* Title on left */}
                                      <div className="TaskCardTitle">
                                        {task.title || 'Untitled task'}
                                      </div>

                                      {/* Icons on right */}
                                      <div className="TaskCardIcons">
                                        {/* Due date indicator */}
                                        {task.due_date && (() => {
                                          // Parse date string (YYYY-MM-DD) to Date object
                                          let dateStr = String(task.due_date);
                                          // Extract just the date part if it includes time/timezone
                                          if (dateStr.includes('T')) {
                                            dateStr = dateStr.split('T')[0];
                                          }

                                          // Parse as local date (YYYY-MM-DD)
                                          const dateObj = new Date(dateStr + 'T00:00:00');

                                          // Check if valid date
                                          if (isNaN(dateObj.getTime())) {
                                            return null;
                                          }

                                          // Compare timestamps: task due date vs today at midnight
                                          const today = new Date();
                                          today.setHours(0, 0, 0, 0);
                                          const isOverdue = dateObj.getTime() < today.getTime() && task.status !== 'done';
                                          const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                                          return (
                                            <span
                                              className="TaskCardDueDateBadge"
                                              title={`Due: ${dateObj.toLocaleDateString()}`}
                                              style={{
                                                color: isOverdue ? '#ef4444' : '#10b981'
                                              }}
                                            >
                                              📅 {formattedDate}
                                            </span>
                                          );
                                        })()}

                                        {/* Comment count indicator */}
                                        {task.comment_count > 0 && (
                                          <span
                                            className={`CommentCountBadge ${task.unread_comment_count > 0 ? 'has-unread' : ''}`}
                                            title={`${task.comment_count} comment${task.comment_count > 1 ? 's' : ''}${task.unread_comment_count > 0 ? ` (${task.unread_comment_count} unread)` : ''}`}
                                          >
                                            💬 {task.comment_count}
                                          </span>
                                        )}

                                        {/* Media Panel button */}
                                        {task.media_panel?.enabled && (
                                          <button
                                            className="MediaPanelButton"
                                            title="Open media panel"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenMediaPanelTask(task);
                                            }}
                                          >
                                            📎
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    {/* Label badges */}
                                    {task.labels && task.labels.length > 0 && (
                                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginTop: 6, marginBottom: 6 }}>
                                        {task.labels.map((label) => (
                                          <span
                                            key={label.id}
                                            style={{
                                              display: 'inline-block',
                                              padding: '2px 6px',
                                              borderRadius: 3,
                                              backgroundColor: label.color,
                                              color: '#fff',
                                              fontSize: 10,
                                              fontWeight: 500,
                                            }}
                                          >
                                            {label.name}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    {/* Render checklist + notes inline; stop click so the modal doesn't pop */}
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <ChecklistInline task={task} onPatchTask={onPatchTask} />
                                    </div>

                                    {/* Status row */}
                                    <div className="TaskMetaRow">
                                      <label className="TaskMetaLabel">Status:</label>
                                      <select
                                        className="TaskStatusSelect"
                                        // FIX: Use normaliseStatus for consistent value/display
                                        value={normaliseStatus(task.status)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          onChangeStatus(task.id, e.target.value);
                                        }}
                                        aria-label="Change task status"
                                      >
                                        {STATUSES.map((s) => (
                                          <option key={s.key} value={s.key}>
                                            {s.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {/* Created by */}
                                    <div className="TaskMetaRow small">
                                      <div className="TaskMetaLabel">Created by:</div>
                                      <div className="TaskMetaValue">{task.created_by_user_id || '—'}</div>
                                    </div>

                                    {/* Assigned to */}
                                    {task.assigned_to_user_id && (() => {
                                      // Find assignee name from collaborators
                                      const assignee = collaborators?.find(c => c.id === task.assigned_to_user_id);
                                      const assigneeName = assignee?.name || assignee?.email || `User ${task.assigned_to_user_id}`;

                                      return (
                                        <div className="TaskMetaRow small">
                                          <div className="TaskMetaLabel">Assigned to:</div>
                                          <div className="TaskMetaValue">{assigneeName}</div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                              </SortableTask>
                            );
                          })}
                        </SortableContext>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Drag Overlay - shows a preview of the dragged task that follows cursor */}
              <DragOverlay dropAnimation={null}>
                {activeId ? (() => {
                  const activeTask = tasks.find((t) => t.id === activeId);
                  if (!activeTask) return null;

                  return (
                    <div className="TaskCard" style={{
                      cursor: 'grabbing',
                      opacity: 0.9,
                      transform: 'rotate(-2deg)',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                      ...getCardBackgroundStyle(activeTask)
                    }}>
                      <div className="TaskCardTitle" style={{ cursor: 'grabbing' }}>
                        {activeTask.title || 'Untitled task'}
                      </div>

                      {/* Label badges */}
                      {activeTask.labels && activeTask.labels.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                          {activeTask.labels.map((label) => (
                            <span
                              key={label.id}
                              style={{
                                display: 'inline-block',
                                padding: '2px 6px',
                                borderRadius: 3,
                                backgroundColor: label.color,
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 500,
                              }}
                            >
                              {label.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })() : null}
              </DragOverlay>
            </> {/* <-- Closing fragment tag */}
          </DndContext>
        </>
      )}

      {/* Media Panel Modal */}
      {openMediaPanelTask && openMediaPanelTask.media_panel?.enabled && (
        <>
          {/* Backdrop */}
          <div
            className="MediaPanel-backdrop"
            onClick={() => setOpenMediaPanelTask(null)}
          />
          {/* Panel */}
          <MediaPanel
            mediaPanel={sanitizeMediaPanel(openMediaPanelTask.media_panel)}
            onClose={() => setOpenMediaPanelTask(null)}
          />
        </>
      )}
    </main>
  );
}